import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, ElementRef, Injector, afterNextRender, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';

import { AuthService } from '@auth0/auth0-angular';

import { filter, take } from 'rxjs';

import { legalUrl } from '@core/services/legal-links';
import { ProfileService } from '@core/services/profile.service';
import { TenantOverrideService } from '@core/services/tenant-override.service';
import { ThemeService } from '@core/services/theme.service';
import { VersionService } from '@core/services/version.service';

import { type PortalPage } from '@models/page-permission.model';

import { environment } from '@environments/environment';

/**
 * Viewport at which the sidebar turns into an off-canvas drawer behind a top bar. Must match the
 * `@media (max-width: 768px)` rules in this file's styles and in app.scss (the shell layout).
 */
const MOBILE_QUERY = '(max-width: 768px)';

/**
 * Left sidebar nav for the Chef/Admin app shell (see app.ts — hidden on /auth/callback and
 * /access-denied, the two routes that render outside the authenticated app proper).
 * Absences/Shifts links are feature-gated the same way their routes are (featureGuard) so the
 * nav never shows a link that would immediately bounce to /access-denied.
 *
 * On mobile widths the sidebar is replaced by a top bar with a menu button; the same nav slides
 * in as a drawer over a backdrop. The top bar, backdrop and close button do not exist on desktop.
 */
@Component({
  selector: 'app-nav',
  imports: [RouterLink, RouterLinkActive, AsyncPipe],
  host: {
    '(document:keydown.escape)': 'closeMenu(true)',
  },
  template: `
    <header class="app-mobile-bar">
      <button
        #menuToggle
        type="button"
        class="app-mobile-toggle"
        aria-controls="app-nav-drawer"
        [attr.aria-expanded]="open()"
        aria-label="Menü öffnen"
        (click)="openMenu()"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
          <path d="M4 7h16M4 12h16M4 17h16" stroke-linecap="round" />
        </svg>
      </button>
      <img src="/assets/img/logo-dark.png" alt="Chroniq" class="app-mobile-logo" />
      <button
        type="button"
        class="app-mobile-theme"
        [attr.aria-label]="isDark() ? 'Helles Design einschalten' : 'Dunkles Design einschalten'"
        (click)="theme.toggle()"
      >
        @if (isDark()) {
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
            <circle cx="12" cy="12" r="4.25" />
            <path d="M12 2.5v2.25M12 19.25v2.25M4.6 4.6l1.6 1.6M17.8 17.8l1.6 1.6M2.5 12h2.25M19.25 12h2.25M4.6 19.4l1.6-1.6M17.8 6.2l1.6-1.6" stroke-linecap="round" />
          </svg>
        } @else {
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
            <path d="M20.5 14.2A8.5 8.5 0 0 1 9.8 3.5a8.5 8.5 0 1 0 10.7 10.7z" stroke-linejoin="round" />
          </svg>
        }
      </button>
    </header>

    <button
      type="button"
      class="app-nav-backdrop"
      [class.open]="open()"
      tabindex="-1"
      aria-label="Menü schliessen"
      (click)="closeMenu()"
    ></button>

    <nav #drawer id="app-nav-drawer" class="app-nav" [class.open]="open()" aria-label="Hauptnavigation">
      <div class="app-nav-brand">
        <img src="/assets/img/logo-dark.png" alt="Chroniq" class="app-nav-logo" />
        <button type="button" class="app-nav-close" aria-label="Menü schliessen" (click)="closeMenu(true)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <ul class="app-nav-links">
        @if (visible().has('dashboard')) {
          <li>
            <a routerLink="/dashboard" routerLinkActive="active" ariaCurrentWhenActive="page" (click)="closeMenu()">
              <svg class="app-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
                <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
                <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
                <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
              </svg>
              <span>Dashboard</span>
            </a>
          </li>
        }
        @if (visible().has('employees')) {
          <li>
            <a routerLink="/employees" routerLinkActive="active" ariaCurrentWhenActive="page" (click)="closeMenu()">
              <svg class="app-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                <circle cx="9" cy="8" r="3.25" />
                <path d="M3.5 20c0-3.6 2.46-6 5.5-6s5.5 2.4 5.5 6" stroke-linecap="round" />
                <circle cx="17" cy="8.5" r="2.5" />
                <path d="M15.5 14.25c2.6.35 4.5 2.55 4.5 5.75" stroke-linecap="round" />
              </svg>
              <span>Mitarbeitende</span>
            </a>
          </li>
        }
        @if (visible().has('time')) {
          <li>
            <a routerLink="/time" routerLinkActive="active" ariaCurrentWhenActive="page" (click)="closeMenu()">
              <svg class="app-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                <rect x="4" y="3.5" width="16" height="17" rx="2" />
                <path d="M8 8.5h8M8 12h8M8 15.5h4.5" stroke-linecap="round" />
              </svg>
              <span>Zeiten</span>
            </a>
          </li>
        }
        @if (absencesEnabled$ | async) {
          @if (visible().has('absences')) {
            <li>
              <a routerLink="/absences" routerLinkActive="active" ariaCurrentWhenActive="page" (click)="closeMenu()">
                <svg class="app-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                  <rect x="3.5" y="4.5" width="17" height="16" rx="2" />
                  <path d="M3.5 9.5h17" stroke-linecap="round" />
                  <path d="M8 3v3M16 3v3" stroke-linecap="round" />
                  <path d="M7.5 13.5h3M7.5 17h3M13.5 13.5h3M13.5 17h3" stroke-linecap="round" />
                </svg>
                <span>Abwesenheiten</span>
              </a>
            </li>
          }
        }
        @if (shiftsEnabled$ | async) {
          @if (visible().has('shifts')) {
            <li>
              <a routerLink="/shifts" routerLinkActive="active" ariaCurrentWhenActive="page" (click)="closeMenu()">
                <svg class="app-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                  <circle cx="12" cy="12" r="8.5" />
                  <path d="M12 7v5l3.5 2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <span>Schichtplanung</span>
              </a>
            </li>
          }
        }
        @if (swapsEnabled$ | async) {
          @if (visible().has('shiftSwaps')) {
            <li>
              <a routerLink="/shift-swaps" routerLinkActive="active" ariaCurrentWhenActive="page" (click)="closeMenu()">
                <svg class="app-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                  <path d="M5 8h13m0 0-3-3m3 3-3 3" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M19 16H6m0 0 3-3m-3 3 3 3" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <span>Schichttausch</span>
              </a>
            </li>
          }
        }
        @if (visible().has('monthClose')) {
          <li>
            <a routerLink="/month-close" routerLinkActive="active" ariaCurrentWhenActive="page" (click)="closeMenu()">
              <svg class="app-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                <rect x="4" y="5.5" width="16" height="14.5" rx="2" />
                <path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" stroke-linecap="round" />
                <path d="m9 15 2.2 2.2L15.5 13" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span>Monatsabschluss</span>
            </a>
          </li>
        }
        @if (visible().has('exports')) {
          <li>
            <a routerLink="/exports" routerLinkActive="active" ariaCurrentWhenActive="page" (click)="closeMenu()">
              <svg class="app-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                <path d="M12 3.5v11" stroke-linecap="round" />
                <path d="M7.5 10.5 12 15l4.5-4.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M4.5 16v2.5a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V16" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span>Exporte</span>
            </a>
          </li>
        }
        @if (visible().has('terminals')) {
          <li>
            <a routerLink="/terminals" routerLinkActive="active" ariaCurrentWhenActive="page" (click)="closeMenu()">
              <svg class="app-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                <rect x="5" y="3.5" width="14" height="17" rx="2.5" />
                <path d="M9.5 8.5a3.5 3.5 0 0 1 5 0M8 6.5a6 6 0 0 1 8 0" stroke-linecap="round" />
                <path d="M10 16.5h4" stroke-linecap="round" />
              </svg>
              <span>Terminals</span>
            </a>
          </li>
        }
        @if (visible().has('settings')) {
          <li>
            <a routerLink="/settings" routerLinkActive="active" ariaCurrentWhenActive="page" (click)="closeMenu()">
              <svg class="app-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                <path d="M4 7h9M17 7h3" stroke-linecap="round" />
                <circle cx="14" cy="7" r="2" />
                <path d="M4 12h3M11 12h9" stroke-linecap="round" />
                <circle cx="8" cy="12" r="2" />
                <path d="M4 17h9M17 17h3" stroke-linecap="round" />
                <circle cx="14" cy="17" r="2" />
              </svg>
              <span>Einstellungen</span>
            </a>
          </li>
        }
      </ul>

      <div class="app-nav-footer">
        @if (tenantOverride.activeTenantId(); as tenantId) {
          <div class="app-nav-impersonation">
            <span>Mandanten-Ansicht aktiv</span>
            <button type="button" [disabled]="resettingTenant()" [attr.aria-busy]="resettingTenant() || null" (click)="resetTenant()">
              {{ resettingTenant() ? 'Wird zurückgesetzt…' : 'Zurücksetzen' }}
            </button>
          </div>
        }
        @if (auth0.user$ | async; as user) {
          <div class="app-nav-user">
            <svg class="app-nav-user-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
              <circle cx="12" cy="8" r="3.25" />
              <path d="M5.5 20c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5" stroke-linecap="round" />
            </svg>
            <span>{{ (displayName$ | async) || user.email }}</span>
          </div>
        }
        <button
          type="button"
          class="app-nav-theme"
          role="switch"
          [attr.aria-checked]="isDark()"
          (click)="theme.toggle()"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
            <path d="M20.5 14.2A8.5 8.5 0 0 1 9.8 3.5a8.5 8.5 0 1 0 10.7 10.7z" stroke-linejoin="round" />
          </svg>
          <span class="app-nav-theme-label">Dunkelmodus</span>
          <span class="app-nav-switch" aria-hidden="true"></span>
        </button>
        <button type="button" class="app-nav-logout" (click)="logout()">Abmelden</button>

        <nav class="app-nav-legal" aria-label="Rechtliches">
          @if (appSiteUrl) {
            <a class="app-nav-site" [href]="appSiteUrl" target="_blank" rel="noopener noreferrer">Website</a>
          }
          <a [href]="imprintUrl" target="_blank" rel="noopener noreferrer">Impressum</a>
          <a [href]="privacyUrl" target="_blank" rel="noopener noreferrer">Datenschutz</a>
          <a [href]="termsUrl" target="_blank" rel="noopener noreferrer">AGB</a>
        </nav>
        <p class="app-nav-version">v{{ spaVersion }}@if (apiVersion$ | async; as apiVersion) { · API v{{ apiVersion }} }</p>
      </div>
    </nav>
  `,
  styles: `
    /* Mobile-only chrome, see the media query at the end of this block. */
    .app-mobile-bar,
    .app-mobile-theme,
    .app-nav-backdrop,
    .app-nav-close {
      display: none;
    }

    .app-nav {
      display: flex;
      flex-direction: column;
      width: 240px;
      flex-shrink: 0;
      height: 100%;
      background: var(--color-nav-bg);
      color: #fff;
      padding: var(--s-6) 0;
    }

    .app-nav-brand {
      margin-bottom: var(--s-8);
      padding: 0 var(--s-6);
      display: flex;
      justify-content: center;
    }

    .app-nav-logo {
      width: 100%;
      max-width: 180px;
      height: auto;
      display: block;
    }

    .app-nav-links {
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: var(--s-1);
      flex: 1;
    }

    .app-nav-links a {
      display: flex;
      align-items: center;
      gap: var(--s-3);
      padding: var(--s-3) var(--s-6);
      border-left: 3px solid transparent;
      color: rgba(255, 255, 255, 0.75);
      text-decoration: none;
      font-size: 14.5px;
      /* Active state fakes bold via text-shadow instead of switching font-weight: weight
         jumps between the Light (300) and heading (600) font files snap instantly (no two
         static font files can be interpolated), which reads as a brittle "flash" on switch.
         text-shadow's color/blur do interpolate, so this stays smooth. */
      text-shadow: 0 0 0 transparent;
      transition: background-color var(--t-fast), color var(--t-fast), border-color var(--t-fast), text-shadow var(--t-fast);
    }

    .app-nav-icon {
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }

    .app-nav-links a:hover {
      background: rgba(255, 255, 255, 0.08);
      color: #fff;
    }

    .app-nav-links a.active {
      background: rgba(255, 255, 255, 0.12);
      border-left-color: var(--color-accent);
      color: #fff;
      text-shadow: 0 0 0.6px currentColor;
    }

    .app-nav-footer {
      border-top: 1px solid rgba(255, 255, 255, 0.15);
      margin-top: var(--s-4);
      padding: var(--s-4) var(--s-6) 0;
      display: flex;
      flex-direction: column;
      gap: var(--s-3);
    }

    .app-nav-impersonation {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--s-2);
      padding: var(--s-2) var(--s-2);
      background: rgba(255, 255, 255, 0.12);
      border-radius: var(--r-sm);
      font-size: 12px;

      span {
        color: #fff;
        font-weight: var(--font-weight-heading);
      }

      button {
        background: transparent;
        border: none;
        color: rgba(255, 255, 255, 0.8);
        text-decoration: underline;
        cursor: pointer;
        font-size: 12px;
        padding: 0;
      }
    }

    .app-nav-user {
      display: flex;
      align-items: center;
      gap: var(--s-2);
      font-size: 13px;
      color: rgba(255, 255, 255, 0.7);
      padding: 0 var(--s-2);

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .app-nav-user-icon {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }

    .app-nav-theme {
      display: flex;
      align-items: center;
      gap: var(--s-2);
      padding: var(--s-2);
      border-radius: var(--r-sm);
      font-size: 13px;
      color: rgba(255, 255, 255, 0.7);
      text-align: left;
      transition: background-color var(--t-fast), color var(--t-fast);

      svg {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.08);
        color: #fff;
      }
    }

    .app-nav-theme-label {
      flex: 1;
    }

    .app-nav-switch {
      position: relative;
      width: 32px;
      height: 18px;
      border-radius: var(--r-full);
      background: rgba(255, 255, 255, 0.25);
      transition: background-color var(--t-fast);

      &::after {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: #fff;
        transition: transform var(--t-fast);
      }
    }

    .app-nav-theme[aria-checked='true'] .app-nav-switch {
      background: var(--color-accent);

      &::after {
        transform: translateX(14px);
      }
    }

    .app-nav-logout {
      background: transparent;
      border: 1px solid rgba(255, 255, 255, 0.3);
      color: #fff;
      border-radius: var(--r-md);
      padding: var(--s-2) var(--s-4);
      cursor: pointer;
      transition: background-color var(--t-fast);
    }

    .app-nav-logout:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .app-nav-legal {
      display: flex;
      flex-wrap: wrap;
      gap: var(--s-1) var(--s-3);
    }

    .app-nav-legal a {
      font-size: 11.5px;
      color: rgba(255, 255, 255, 0.45);
      text-decoration: none;
    }

    .app-nav-version {
      margin: var(--s-2) 0 0;
      font-size: 11px;
      color: rgba(255, 255, 255, 0.3);
      overflow-wrap: anywhere;
    }

    .app-nav-legal a:hover {
      color: rgba(255, 255, 255, 0.8);
      text-decoration: underline;
    }

    /* Keep the breakpoint in sync with MOBILE_QUERY and app.scss. */
    @media (max-width: 768px) {
      .app-mobile-bar {
        display: flex;
        align-items: center;
        gap: var(--s-2);
        height: 56px;
        padding: 0 var(--s-3);
        background: var(--color-nav-bg);
        box-shadow: var(--shadow-md);
      }

      .app-mobile-logo {
        height: 26px;
        width: auto;
      }

      .app-mobile-toggle,
      .app-mobile-theme,
      .app-nav-close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        border-radius: var(--r-md);
        color: #fff;
        transition: background-color var(--t-fast);

        svg {
          width: 24px;
          height: 24px;
        }

        &:hover,
        &:active {
          background: rgba(255, 255, 255, 0.12);
        }
      }

      .app-mobile-theme {
        margin-left: auto;
      }

      /* The top bar carries the theme button on mobile, so the drawer's switch would be a duplicate. */
      .app-nav-theme {
        display: none;
      }

      .app-nav-close {
        position: absolute;
        top: var(--s-3);
        right: var(--s-3);
      }

      /* Off-canvas drawer: visibility (not just the transform) hides the closed drawer from
         keyboard and screen readers, delayed on close so the slide-out stays visible. */
      .app-nav {
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        z-index: 1100;
        width: min(300px, 85vw);
        height: auto;
        overflow-y: auto;
        box-shadow: var(--shadow-xl);
        transform: translateX(-100%);
        visibility: hidden;
        transition: transform var(--t-base), visibility 0s linear var(--t-base);
      }

      .app-nav.open {
        transform: none;
        visibility: visible;
        transition: transform var(--t-base), visibility 0s;
      }

      .app-nav-brand {
        justify-content: flex-start;
        margin-bottom: var(--s-6);
      }

      .app-nav-logo {
        max-width: 150px;
      }

      .app-nav-backdrop {
        display: block;
        position: fixed;
        inset: 0;
        z-index: 1090;
        background: rgba(15, 21, 32, 0.5);
        opacity: 0;
        visibility: hidden;
        transition: opacity var(--t-base), visibility 0s linear var(--t-base);
      }

      .app-nav-backdrop.open {
        opacity: 1;
        visibility: visible;
        transition: opacity var(--t-base), visibility 0s;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .app-nav,
      .app-nav.open,
      .app-nav-backdrop,
      .app-nav-backdrop.open {
        transition: none;
      }
    }
  `,
})
export class Nav {
  protected readonly auth0 = inject(AuthService);
  private readonly profile = inject(ProfileService);
  protected readonly tenantOverride = inject(TenantOverrideService);
  protected readonly theme = inject(ThemeService);
  protected readonly isDark = computed(() => this.theme.theme() === 'dark');

  protected readonly absencesEnabled$ = this.profile.isFeatureEnabled('Absences');
  protected readonly shiftsEnabled$ = this.profile.isFeatureEnabled('ShiftPlanning');
  protected readonly swapsEnabled$ = this.profile.isFeatureEnabled('ShiftSwap');
  /** Pages the user may open (permission none/read/write per page, set by an admin); drives which links show. */
  private readonly readable = toSignal(this.profile.readablePages$(), { initialValue: [] as PortalPage[] });
  protected readonly visible = computed(() => new Set<PortalPage>(this.readable()));
  protected readonly displayName$ = this.profile.displayName$();

  /** Public Chroniq website (App-SPA); empty in an environment without one, which hides the link. */
  protected readonly appSiteUrl: string = environment.appSiteUrl;

  protected readonly imprintUrl = legalUrl('imprint');
  protected readonly privacyUrl = legalUrl('privacy');
  protected readonly termsUrl = legalUrl('terms');

  private readonly versions = inject(VersionService);
  protected readonly spaVersion = this.versions.spaVersion;
  protected readonly apiVersion$ = this.versions.apiVersion$;

  protected readonly resettingTenant = signal(false);

  /** Mobile drawer state; has no effect on the desktop sidebar, which is always visible. */
  protected readonly open = signal(false);

  private readonly injector = inject(Injector);
  private readonly menuToggle = viewChild.required<ElementRef<HTMLButtonElement>>('menuToggle');
  private readonly drawer = viewChild.required<ElementRef<HTMLElement>>('drawer');

  constructor() {
    // An override the server ignored (tenant gone, membership revoked, other account) must not
    // keep claiming "Mandanten-Ansicht aktiv" for data that is the default tenant's.
    this.profile
      .servedTenantId$()
      .pipe(filter((served) => served !== undefined), take(1), takeUntilDestroyed())
      .subscribe((served) => this.tenantOverride.dropIfIgnored(served ?? null));

    // Growing past the breakpoint with the drawer open would otherwise leave it open for the
    // next time the viewport shrinks again. matchMedia is missing in jsdom (unit tests).
    const mobile = window.matchMedia?.(MOBILE_QUERY);
    if (mobile) {
      const onChange = (event: MediaQueryListEvent): void => {
        if (!event.matches) {
          this.open.set(false);
        }
      };
      mobile.addEventListener('change', onChange);
      inject(DestroyRef).onDestroy(() => mobile.removeEventListener('change', onChange));
    }
  }

  protected openMenu(): void {
    this.open.set(true);
    // The drawer only becomes focusable after this render; moving focus in keeps keyboard and
    // screen-reader users inside the menu they just opened.
    afterNextRender(() => this.drawer().nativeElement.querySelector<HTMLElement>('a')?.focus(), {
      injector: this.injector,
    });
  }

  /** `restoreFocus` hands focus back to the menu button (Escape, close button); a link click or
   *  backdrop tap leaves it where the navigation / the user put it. */
  protected closeMenu(restoreFocus = false): void {
    if (!this.open()) {
      return;
    }
    this.open.set(false);
    if (restoreFocus) {
      this.menuToggle().nativeElement.focus();
    }
  }

  logout(): void {
    this.tenantOverride.clearSilently();
    this.auth0.logout({ logoutParams: { returnTo: window.location.origin } });
  }

  protected resetTenant(): void {
    // The page reloads right away; the busy state stops a second click and shows that something happens.
    this.resettingTenant.set(true);
    this.tenantOverride.switchTo(null);
  }
}
